import * as React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { baseUrl } from "../../utils/baseUrl";
import { getAuthorizationHeaderObject } from "../../utils/authHeaders";

/**
 * Master-admin partner branding logo control for the partner settings modal.
 *
 * Layout deliberately mirrors the existing brand-color row: a left-aligned
 * label, the active control to its right, with helper text directly below.
 *
 * The component is uncontrolled for the staged S3 id — parents own the value
 * via `value` (the S3 key persisted on the partner) and `onChange` (called
 * with the new key, or `null` for a staged removal). It performs the upload
 * synchronously so the parent only ever sees a final S3 id, never a pending
 * blob. A failed upload is surfaced inline so the rest of the form can still
 * save.
 */
export const ACCEPTED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
];

/** Match the backend thumbnail cap (~1.5 MB) to surface rejects before the network round-trip. */
export const MAX_LOGO_BYTES = Math.round(1.5 * 1024 * 1024);

const ACCEPT_ATTR = ACCEPTED_LOGO_TYPES.join(",");

function formatMb(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PartnerLogoUpload({
  value,
  previewUrl,
  onChange,
  disabled = false,
  uploader,
}) {
  const fileInputRef = React.useRef(null);
  // Guard against missing provider in tests or storybook contexts.
  const { currentUserSessionStorage } = useGlobalAuthContext() ?? {};

  const [stagedPreview, setStagedPreview] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset the staged preview when the parent clears the value (e.g. Save
  // succeeded and the form re-mounts with empty initialValues).
  React.useEffect(() => {
    if (!value) setStagedPreview(null);
  }, [value]);

  const effectivePreview = stagedPreview || (value ? previewUrl : null);

  const handlePickFile = React.useCallback(() => {
    if (disabled || uploading) return;
    setError(null);
    if (fileInputRef.current) fileInputRef.current.click();
  }, [disabled, uploading]);

  const performUpload = React.useCallback(
    async (file) => {
      if (uploader) return uploader(file);
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${baseUrl}/g/fileUpload?generateSizes=true`,
        formData,
        {
          headers: {
            ...getAuthorizationHeaderObject(currentUserSessionStorage),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response?.data ?? {};
    },
    [uploader, currentUserSessionStorage]
  );

  const handleFile = React.useCallback(
    async (file) => {
      if (!file) return;

      if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
        setError(
          "Unsupported file type. Use PNG, JPEG, SVG, or WebP."
        );
        return;
      }

      if (file.size > MAX_LOGO_BYTES) {
        setError(
          `Logo is too large (max ${formatMb(MAX_LOGO_BYTES)}). Pick a smaller image.`
        );
        return;
      }

      setError(null);
      setUploading(true);
      try {
        const data = await performUpload(file);
        const id = data?.id;
        const url = data?.url;
        if (!id) {
          throw new Error("Upload response missing id");
        }
        if (url) setStagedPreview(url);
        onChange?.(id);
      } catch (err) {
        const message =
          typeof err?.response?.data === "string"
            ? err.response.data
            : err?.message || "Upload failed. Try again.";
        setError(message);
      } finally {
        setUploading(false);
      }
    },
    [onChange, performUpload]
  );

  const handleNativeChange = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      // Reset the input so picking the same file twice still triggers onChange.
      event.target.value = "";
      await handleFile(file);
    },
    [handleFile]
  );

  const handleRemove = React.useCallback(() => {
    if (disabled || uploading) return;
    setError(null);
    setStagedPreview(null);
    onChange?.(null);
  }, [disabled, uploading, onChange]);

  const showRemove = Boolean(value) && !uploading;

  return (
    <Stack mt={2} spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <label
          htmlFor="partner-logo-upload"
          style={{ marginRight: "10px", minWidth: 100 }}
        >
          Partner Logo:
        </label>

        {effectivePreview ? (
          <Box
            sx={{
              width: 64,
              height: 64,
              border: "1px solid #ccc",
              borderRadius: "6px",
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={effectivePreview}
              alt="Partner logo preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 64,
              height: 64,
              border: "1px dashed #ccc",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.disabled",
            }}
          >
            <FileUploadOutlinedIcon fontSize="small" />
          </Box>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handlePickFile}
            disabled={disabled || uploading}
            startIcon={
              uploading ? (
                <CircularProgress size={14} aria-label="Uploading logo" />
              ) : (
                <FileUploadOutlinedIcon fontSize="small" />
              )
            }
          >
            {uploading
              ? "Uploading…"
              : effectivePreview
              ? "Replace"
              : "Upload"}
          </Button>

          {showRemove && (
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={handleRemove}
              disabled={disabled || uploading}
              sx={{ color: "text.secondary" }}
            >
              Remove
            </Button>
          )}
        </Stack>

        <input
          id="partner-logo-upload"
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTR}
          onChange={handleNativeChange}
          disabled={disabled || uploading}
          aria-label="Partner logo file input"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      </Stack>

      {error ? (
        <Typography
          role="alert"
          variant="caption"
          color="error"
          sx={{ ml: "110px" }}
        >
          {error}
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "110px" }}
        >
          PNG, JPEG, SVG, or WebP up to {formatMb(MAX_LOGO_BYTES)}.
        </Typography>
      )}
    </Stack>
  );
}

PartnerLogoUpload.propTypes = {
  /** S3 key currently staged on the partner. `null`/`undefined` means no logo. */
  value: PropTypes.string,
  /** Optional URL used to render the existing logo preview. */
  previewUrl: PropTypes.string,
  /** Called with the new S3 key (or `null` for removal). */
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /**
   * Optional uploader override — receives the selected `File` and resolves to
   * `{ id, url }`. Provided for testing so we don't depend on axios + auth
   * context in component tests.
   */
  uploader: PropTypes.func,
};
